/**
 * WeaveIn.ai Platform - Document Requirements Checklist
 * Shows required documents for an enrollment/referral/program with status tracking.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, Circle, AlertTriangle, Clock, XCircle, Upload, Shield,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getWeaveinApiUrl } from '@/src/config';

interface DocRequirement {
  id: number;
  doc_type_id: number;
  doc_type_name: string;
  category_name: string;
  is_required: boolean;
  due_date: string | null;
  status: 'pending' | 'satisfied' | 'overdue' | 'waived';
  satisfied_by_doc_id: number | null;
  waived_reason: string | null;
}

interface DocumentRequirementsProps {
  context: 'enrollment' | 'referral' | 'program';
  contextId: number;
  onUploadForType?: (docTypeId: number, docTypeName: string) => void;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
};

export function DocumentRequirements({ context, contextId, onUploadForType }: DocumentRequirementsProps) {
  const [requirements, setRequirements] = useState<DocRequirement[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRequirements = useCallback(async () => {
    try {
      const res = await fetch(
        getWeaveinApiUrl(`/api/documents/requirements/${context}/${contextId}`),
        { credentials: 'include', headers: getAuthHeaders() },
      );
      if (res.ok) {
        setRequirements(await res.json());
      }
    } catch (e) {
      console.error('Error fetching requirements:', e);
    } finally {
      setLoading(false);
    }
  }, [context, contextId]);

  useEffect(() => {
    if (contextId) fetchRequirements();
  }, [fetchRequirements, contextId]);

  if (loading || requirements.length === 0) return null;

  const satisfied = requirements.filter((r) => r.status === 'satisfied' || r.status === 'waived').length;
  const total = requirements.length;
  const allComplete = satisfied === total;

  const statusIcon = (status: string) => {
    switch (status) {
      case 'satisfied': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'waived': return <Shield className="w-4 h-4 text-gray-400" />;
      case 'overdue': return <AlertTriangle className="w-4 h-4 text-red-600" />;
      default: return <Circle className="w-4 h-4 text-gray-300" />;
    }
  };

  return (
    <div className="mb-6 border rounded-lg p-4 bg-muted/30">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-sm">Required Documents</h4>
          <Badge variant={allComplete ? 'default' : 'secondary'} className={allComplete ? 'bg-green-100 text-green-800' : ''}>
            {satisfied}/{total} complete
          </Badge>
        </div>
      </div>

      <div className="space-y-2">
        {requirements.map((req) => (
          <div key={req.id} className="flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-muted/50">
            <div className="flex items-center gap-2.5">
              {statusIcon(req.status)}
              <div>
                <span className={`text-sm ${req.status === 'satisfied' ? 'line-through text-muted-foreground' : 'font-medium'}`}>
                  {req.doc_type_name}
                </span>
                <span className="text-xs text-muted-foreground ml-2">{req.category_name}</span>
                {req.due_date && req.status === 'pending' && (
                  <span className="text-xs text-amber-600 ml-2 flex items-center gap-0.5 inline-flex">
                    <Clock className="w-3 h-3" /> Due {new Date(req.due_date).toLocaleDateString()}
                  </span>
                )}
                {req.status === 'waived' && (
                  <span className="text-xs text-muted-foreground ml-2">(Waived{req.waived_reason ? `: ${req.waived_reason}` : ''})</span>
                )}
              </div>
            </div>
            {req.status === 'pending' && onUploadForType && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => onUploadForType(req.doc_type_id, req.doc_type_name)}
              >
                <Upload className="w-3 h-3 mr-1" /> Upload
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
